import { useState, useCallback, useRef } from 'react';
import { attachmentsApi } from '@/lib/api';
import { StagedFile, DemotedEmbed } from '@/components/ui/RichTextEditor';
import { extractEmbeddedAttachmentIds } from '@/lib/embeds';

interface UseFileAttachmentsOptions {
  organizationId: string;
  entityType: 'taskComment' | 'task' | 'message' | 'directMessage';
  /**
   * Needed for inline image embeds: they upload the moment they are pasted,
   * before the message exists, as `messageDraft` attachments on this project.
   */
  projectId?: string;
}

export interface UploadedEmbed {
  attachmentId: string;
  url: string;
}

interface UseFileAttachmentsReturn {
  stagedFiles: StagedFile[];
  addFiles: (files: File[]) => void;
  removeFile: (fileId: string) => void;
  clearFiles: () => void;
  /** Upload staged files to the (now existing) entity. Returns every attachment id, including ones already uploaded as drafts. */
  uploadAll: (entityId: string) => Promise<string[]>;
  isUploading: boolean;
  hasFiles: boolean;
  /** Upload an image now (draft) so it can be embedded in the editor. Rejects without a projectId. */
  embedImage: (file: File) => Promise<UploadedEmbed>;
  /** An embed the writer moved back out of the content: keep it staged as a plain attachment. */
  addUploaded: (embed: DemotedEmbed) => void;
  /** Draft attachment ids to link on post: embeds in the content plus demoted ones still staged. */
  collectAttachmentIds: (content: string) => string[];
}

/**
 * Staged file attachments for an editor surface.
 *
 * Two upload paths:
 * - Plain attachments are staged and uploaded after the message exists
 *   (`uploadAll(message.id)`), as they always were.
 * - Embedded images upload immediately (`embedImage`) as `messageDraft`
 *   attachments, so the editor can show them inline; the server links them to
 *   the message on post via `attachmentIds` (`collectAttachmentIds(content)`).
 *
 * @example
 * const attachments = useFileAttachments({ organizationId, entityType: 'message', projectId });
 * const reply = await messagesApi.createReply(id, { content, attachmentIds: attachments.collectAttachmentIds(content) });
 * await attachments.uploadAll(reply.id);
 */
export function useFileAttachments({
  organizationId,
  entityType,
  projectId,
}: UseFileAttachmentsOptions): UseFileAttachmentsReturn {
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // Embeds and uploadAll can overlap; the flag is "anything in flight".
  const inFlight = useRef(0);
  const track = useCallback(<T,>(p: Promise<T>): Promise<T> => {
    inFlight.current += 1;
    setIsUploading(true);
    return p.finally(() => {
      inFlight.current -= 1;
      if (inFlight.current === 0) setIsUploading(false);
    });
  }, []);

  const addFiles = useCallback((files: File[]) => {
    const newFiles = files.map((file) => ({
      id: `staged-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    }));
    setStagedFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const addUploaded = useCallback((embed: DemotedEmbed) => {
    setStagedFiles((prev) => [
      ...prev,
      {
        id: `staged-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        // Already in S3; the File only carries a name and type for the chip.
        file: new File([], embed.fileName, { type: 'image/*' }),
        preview: embed.url,
        attachmentId: embed.attachmentId,
      },
    ]);
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setStagedFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      // Only revoke object URLs we created; a demoted embed's preview is a presigned URL.
      if (file?.preview && !file.attachmentId) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  }, []);

  const clearFiles = useCallback(() => {
    stagedFiles.forEach((f) => {
      if (f.preview && !f.attachmentId) URL.revokeObjectURL(f.preview);
    });
    setStagedFiles([]);
  }, [stagedFiles]);

  const embedImage = useCallback(
    (file: File): Promise<UploadedEmbed> => {
      if (!projectId) return Promise.reject(new Error('Embedding needs a project'));
      return track(
        (async () => {
          const { attachmentId } = await attachmentsApi.uploadFile(file, organizationId, 'messageDraft', projectId);
          const { downloadUrl } = await attachmentsApi.getDownloadUrl(attachmentId);
          return { attachmentId, url: downloadUrl };
        })(),
      );
    },
    [organizationId, projectId, track],
  );

  const collectAttachmentIds = useCallback(
    (content: string): string[] => {
      const staged = stagedFiles.map((f) => f.attachmentId).filter((id): id is string => !!id);
      return [...new Set([...extractEmbeddedAttachmentIds(content), ...staged])];
    },
    [stagedFiles],
  );

  const uploadAll = useCallback(async (entityId: string): Promise<string[]> => {
    if (stagedFiles.length === 0) return [];

    const uploadedIds: string[] = [];
    await track(
      (async () => {
        for (const staged of stagedFiles) {
          // Demoted embeds are already uploaded and are linked via attachmentIds.
          if (staged.attachmentId) {
            uploadedIds.push(staged.attachmentId);
            setStagedFiles((prev) => prev.filter((f) => f.id !== staged.id));
            continue;
          }

          // Mark as uploading
          setStagedFiles((prev) =>
            prev.map((f) => (f.id === staged.id ? { ...f, uploading: true } : f))
          );

          try {
            const { attachmentId } = await attachmentsApi.uploadFile(
              staged.file,
              organizationId,
              entityType,
              entityId
            );
            uploadedIds.push(attachmentId);

            // Remove successfully uploaded file
            setStagedFiles((prev) => prev.filter((f) => f.id !== staged.id));
            if (staged.preview) URL.revokeObjectURL(staged.preview);
          } catch (error) {
            // Mark as error
            setStagedFiles((prev) =>
              prev.map((f) =>
                f.id === staged.id ? { ...f, uploading: false, error: 'Upload failed' } : f
              )
            );
          }
        }
      })(),
    );

    return uploadedIds;
  }, [stagedFiles, organizationId, entityType, track]);

  return {
    stagedFiles,
    addFiles,
    removeFile,
    clearFiles,
    uploadAll,
    isUploading,
    hasFiles: stagedFiles.length > 0,
    embedImage,
    addUploaded,
    collectAttachmentIds,
  };
}
